import { useState, useEffect, useCallback } from 'react';
import { getUserPageSummary, getUsersList, getUserDetail, getUserRoles } from '@/services/api';
import type { UserPageSummary, UserListItem, UserDetail, RolePermissionsItem } from '@/types/user';
import { useDashboardStore } from '@/stores/useDashboardStore';

interface UseUsersParams {
  role?: string;
  flag?: string;
  enabled?: string;
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export function useUserSummary() {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [summary, setSummary] = useState<UserPageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetch() {
      try {
        setLoading(true);
        const data = await getUserPageSummary();
        if (!cancelled) {
          setSummary(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to fetch user summary'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetch();
    return () => { cancelled = true; };
  }, [refreshKey]);

  return { summary, loading, error };
}

export function useUsers(params: UseUsersParams = {}) {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const depsKey = JSON.stringify(params);

  useEffect(() => {
    let cancelled = false;
    async function fetch() {
      try {
        setLoading(true);
        const data = await getUsersList(params);
        if (!cancelled) {
          setUsers(data.users);
          setTotal(data.pagination.total);
          setTotalPages(data.pagination.totalPages);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to fetch users'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetch();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey, refreshKey]);

  return { users, total, totalPages, loading, error };
}

export function useUserRoles() {
  const refreshKey = useDashboardStore((state) => state.refreshKey);
  const [roles, setRoles] = useState<RolePermissionsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetch() {
      try {
        setLoading(true);
        const data = await getUserRoles();
        if (!cancelled) {
          setRoles(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to fetch user roles'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetch();
    return () => { cancelled = true; };
  }, [refreshKey]);

  return { roles, loading, error };
}

export function useUserDetail(userId: string | null) {
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchDetail = useCallback(async (id: string) => {
    try {
      setLoading(true);
      const data = await getUserDetail(id);
      setDetail(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch user detail'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userId) {
      fetchDetail(userId);
    } else {
      setDetail(null);
    }
  }, [userId, fetchDetail]);

  return { detail, loading, error, refetch: fetchDetail };
}

